import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip
} from '@mui/material';
import {
    BugReport as BugReportIcon,
    Build as BuildIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { green, red, orange } from '@mui/material/colors';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/authUtils';

const PathValidationTool = ({ darkMode, themeVariant }) => {
    const [loading, setLoading] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [error, setError] = useState(null);
    const [fixingNodes, setFixingNodes] = useState(new Set());

    const runValidation = async (dryRun = true) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v3/migration/validate-node-paths?dryRun=${dryRun}`,
                {},
                { headers: getAuthHeaders(true) }
            );
            
            setValidationResult(response.data);
        } catch (err) {
            console.error('Path validation error:', err);
            setError(err.response?.data?.error || err.message || 'Path validation failed');
        } finally {
            setLoading(false);
        }
    };

    const fixSingleNode = async (nodeId) => {
        setFixingNodes(prev => new Set([...prev, nodeId]));
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v3/migration/fix-node-path/${nodeId}`,
                {},
                { headers: getAuthHeaders(true) }
            );
            
            // Update the validation result to reflect the fix
            if (response.data.fixed) {
                setValidationResult(prev => ({
                    ...prev,
                    mismatches: prev.mismatches.filter(m => m.nodeId !== nodeId),
                    mismatchCount: prev.mismatchCount - 1,
                    fixedNodes: (prev.fixedNodes || 0) + 1
                }));
            }
        } catch (err) {
            console.error('Fix node error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to fix node');
        } finally {
            setFixingNodes(prev => {
                const newSet = new Set(prev);
                newSet.delete(nodeId);
                return newSet;
            });
        }
    };

    const fixAllNodes = async () => {
        await runValidation(false);
    };

    return (
        <Box sx={{ textAlign: 'left' }}>
            {/* Header and Actions */}
            <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
                <Typography variant="body1" paragraph>
                    This tool validates and fixes path mismatches in the MongoDB filesystem nodes. 
                    Use "Validate Only" to identify issues without making changes, then fix them individually or all at once.
                </Typography>
                
                <Typography variant="body2" paragraph color="warning.main">
                    <strong>Warning:</strong> Path fixes modify the database. Use "Validate Only" first to review mismatches.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => runValidation(true)}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                        {loading ? 'Validating...' : 'Validate Only'}
                    </Button>
                    
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={fixAllNodes}
                        disabled={loading || !validationResult?.mismatches?.length}
                        startIcon={<BuildIcon />}
                    >
                        Fix All Mismatches
                    </Button>
                    
                    {validationResult && (
                        <Button
                            variant="text"
                            onClick={() => runValidation(true)}
                            startIcon={<RefreshIcon />}
                        >
                            Re-validate
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body2">{error}</Typography>
                </Alert>
            )}

            {/* Results Summary */}
            {validationResult && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {validationResult.mismatchCount === 0 ? (
                                <CheckCircleIcon sx={{ color: green[500], mr: 1, fontSize: 24 }} />
                            ) : (
                                <BugReportIcon sx={{ color: red[500], mr: 1, fontSize: 24 }} />
                            )}
                            <Typography variant="h6">
                                Validation Results
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            <Chip 
                                label={`Total Nodes: ${validationResult.totalNodes}`}
                                color="default"
                                variant="outlined"
                            />
                            <Chip 
                                label={`Mismatches: ${validationResult.mismatchCount}`}
                                color={validationResult.mismatchCount > 0 ? "error" : "success"}
                                variant="filled"
                            />
                            {validationResult.fixedNodes > 0 && (
                                <Chip 
                                    label={`Fixed: ${validationResult.fixedNodes}`}
                                    color="success"
                                    variant="filled"
                                />
                            )}
                            {validationResult.dryRun && (
                                <Chip 
                                    label="Dry Run"
                                    color="info"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        {validationResult.mismatchCount === 0 && (
                            <Alert severity="success">
                                All node paths are correct! No issues found.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Mismatches Table */}
            {validationResult?.mismatches?.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Node Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Current Path</TableCell>
                                <TableCell>Expected Path</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {validationResult.mismatches.map((mismatch) => (
                                <TableRow key={mismatch.nodeId}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {mismatch.nodeName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ID: {mismatch.nodeId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={mismatch.isDirectory ? "Folder" : "File"}
                                            size="small"
                                            color={mismatch.isDirectory ? "primary" : "secondary"}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            color="error.main"
                                            sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                        >
                                            {mismatch.currentPath || '<null>'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            color="success.main"
                                            sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                        >
                                            {mismatch.expectedPath}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Fix this node's path">
                                            <IconButton
                                                color="warning"
                                                onClick={() => fixSingleNode(mismatch.nodeId)}
                                                disabled={fixingNodes.has(mismatch.nodeId)}
                                                size="small"
                                            >
                                                {fixingNodes.has(mismatch.nodeId) ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    <BuildIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default PathValidationTool;